import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faArrowRight, faShieldHalved, faChartLine, faGraduationCap, 
  faHandshake, faUsers, faLightbulb, faCheck, faPlay,
  faStar, faQuoteLeft
} from "@fortawesome/free-solid-svg-icons";
import Navbar from "../Components/Navbar";
import Footer from "../Components/Footer";
import useScrollReveal from "../hooks/useScrollReveal";
import "../Styles/Home.css";

function Counter({ end, suffix = "", duration = 2000 }) {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !started) setStarted(true);
    }, { threshold: 0.3 });
    const el = document.getElementById(`counter-${end}-${suffix}`);
    if (el) observer.observe(el);
    return () => { if (el) observer.unobserve(el); };
  }, [end, started, suffix]);

  useEffect(() => {
    if (!started) return;
    let start = 0;
    const step = end / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= end) { setCount(end); clearInterval(timer); }
      else setCount(Math.floor(start));
    }, 16);
    return () => clearInterval(timer);
  }, [started, end, duration]);

  return <span id={`counter-${end}-${suffix}`}>{count}{suffix}</span>;
}

function Home() {
  useScrollReveal();

  return (
    <div className="home-page">
      <Navbar />

      {/* ====== HERO SECTION ====== */}
      <section className="hero-section">
        {/* Animated Background */}
        <div className="hero-bg-wrapper">
          {/* Moving grid */}
          <div className="hero-animated-grid"></div>
          {/* Floating orbs */}
          <div className="hero-orb hero-orb-1"></div>
          <div className="hero-orb hero-orb-2"></div>
          <div className="hero-orb hero-orb-3"></div>
          {/* Animated lines */}
          <div className="hero-lines">
            <div className="hero-line" style={{left: '10%', animationDelay: '0s'}}></div>
            <div className="hero-line" style={{left: '25%', animationDelay: '1.5s'}}></div>
            <div className="hero-line" style={{left: '45%', animationDelay: '3s'}}></div>
            <div className="hero-line" style={{left: '65%', animationDelay: '4.5s'}}></div>
            <div className="hero-line" style={{left: '80%', animationDelay: '6s'}}></div>
            <div className="hero-line" style={{left: '92%', animationDelay: '7.5s'}}></div>
          </div>
          {/* Particle dots */}
          <div className="hero-particles">
            <span className="h-particle" style={{top:'15%', left:'8%', animationDelay:'0s'}}></span>
            <span className="h-particle" style={{top:'25%', left:'85%', animationDelay:'2s'}}></span>
            <span className="h-particle" style={{top:'65%', left:'12%', animationDelay:'4s'}}></span>
            <span className="h-particle" style={{top:'70%', left:'78%', animationDelay:'1s'}}></span>
            <span className="h-particle" style={{top:'40%', left:'55%', animationDelay:'3s'}}></span>
            <span className="h-particle" style={{top:'80%', left:'45%', animationDelay:'5s'}}></span>
          </div>
        </div>

        <div className="container hero-content">
          <div className="hero-text">
            <div className="hero-badge animate-hero-1">
              <span className="hero-badge-dot"></span>
              <span>Trusted by 1,000+ Families Nationwide</span>
            </div>
            <h1 className="hero-title animate-hero-2">
              Building Financial <br/>
              <span className="hero-title-gradient">Security</span> That Lasts <br/>
              Generations.
            </h1>
            <p className="hero-desc animate-hero-3">
              We partner with families to create personalized financial strategies — 
              from life insurance and retirement planning to wealth accumulation — 
              so you can live with confidence and leave a lasting legacy.
            </p>
            <div className="hero-buttons animate-hero-4">
              <Link to="/schedule-appointment" className="btn-primary btn-hero">
                Schedule Free Consultation <FontAwesomeIcon icon={faArrowRight} />
              </Link>
              <Link to="/services" className="btn-outline-light">
                <FontAwesomeIcon icon={faPlay} className="btn-play-icon" /> Watch How It Works
              </Link>
            </div>
            <div className="hero-trust animate-hero-5">
              <div className="hero-trust-item">
                <FontAwesomeIcon icon={faCheck} className="hero-trust-icon" />
                <span>No Obligation</span>
              </div>
              <div className="hero-trust-item">
                <FontAwesomeIcon icon={faCheck} className="hero-trust-icon" />
                <span>45+ States Licensed</span>
              </div>
              <div className="hero-trust-item">
                <FontAwesomeIcon icon={faCheck} className="hero-trust-icon" />
                <span>20+ Years Experience</span>
              </div>
            </div>
          </div>

          {/* Right side — Animated Stats */}
          <div className="hero-visual animate-hero-6">
            <div className="hero-visual-card">
              <div className="hero-visual-glow"></div>
              <div className="hv-stat">
                <span className="hv-stat-num">20+</span>
                <span className="hv-stat-label">Years of Excellence</span>
              </div>
              <div className="hv-divider"></div>
              <div className="hv-stat">
                <span className="hv-stat-num">1,000+</span>
                <span className="hv-stat-label">Families Protected</span>
              </div>
              <div className="hv-divider"></div>
              <div className="hv-stat">
                <span className="hv-stat-num">98%</span>
                <span className="hv-stat-label">Client Satisfaction</span>
              </div>
            </div>
            {/* Floating mini cards */}
            <div className="hero-float-card hf-card-1">
              <div className="hf-icon">🛡️</div>
              <div className="hf-text">
                <strong>Protected</strong>
                <span>$500M+ in Coverage</span>
              </div>
            </div>
            <div className="hero-float-card hf-card-2">
              <div className="hf-icon">⭐</div>
              <div className="hf-text">
                <strong>5.0 Rating</strong>
                <span>Client Reviews</span>
              </div>
            </div>
          </div>
        </div>

        {/* Marquee */}
        <div className="hero-marquee">
          <div className="marquee-track">
            <span>LIFE INSURANCE</span><span className="marquee-dot">◆</span>
            <span>RETIREMENT PLANNING</span><span className="marquee-dot">◆</span>
            <span>WEALTH BUILDING</span><span className="marquee-dot">◆</span>
            <span>FINANCIAL EDUCATION</span><span className="marquee-dot">◆</span>
            <span>KAI-ZEN PLAN</span><span className="marquee-dot">◆</span>
            <span>BUSINESS PROTECTION</span><span className="marquee-dot">◆</span>
            <span>LEGACY PLANNING</span><span className="marquee-dot">◆</span>
            <span>LIFE INSURANCE</span><span className="marquee-dot">◆</span>
            <span>RETIREMENT PLANNING</span><span className="marquee-dot">◆</span>
            <span>WEALTH BUILDING</span><span className="marquee-dot">◆</span>
            <span>FINANCIAL EDUCATION</span><span className="marquee-dot">◆</span>
            <span>KAI-ZEN PLAN</span><span className="marquee-dot">◆</span>
          </div>
        </div>
      </section>

      {/* ====== STATS BAR ====== */}
      <section className="stats-bar reveal">
        <div className="container">
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon-wrap"><FontAwesomeIcon icon={faStar} /></div>
              <div className="stat-content">
                <span className="stat-number"><Counter end={20} suffix="+" /></span>
                <span className="stat-label">Years Excellence</span>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon-wrap"><FontAwesomeIcon icon={faUsers} /></div>
              <div className="stat-content">
                <span className="stat-number"><Counter end={1000} suffix="+" /></span>
                <span className="stat-label">Families Protected</span>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon-wrap"><FontAwesomeIcon icon={faShieldHalved} /></div>
              <div className="stat-content">
                <span className="stat-number"><Counter end={45} suffix="+" /></span>
                <span className="stat-label">States Licensed</span>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon-wrap"><FontAwesomeIcon icon={faChartLine} /></div>
              <div className="stat-content">
                <span className="stat-number"><Counter end={98} suffix="%" /></span>
                <span className="stat-label">Satisfaction Rate</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ====== ABOUT SECTION ====== */}
      <section className="home-about section-padding reveal">
        <div className="container">
          <div className="home-about-grid">
            <div className="home-about-left">
              <span className="section-tag">Who We Are</span>
              <h2 className="section-title">Financial Security Is Not A Luxury — It's A Right.</h2>
              <p className="home-about-text">
                Ebenezer Family Group was founded on a simple belief: every family 
                deserves expert financial guidance. For over two decades, we've 
                been transforming how families think about money, protection, and their future.
              </p>
              <p className="home-about-text">
                We don't just sell products. We educate, strategize, and build 
                relationships that last generations. Our approach starts with 
                understanding YOUR unique situation.
              </p>
              <div className="about-highlights">
                <div className="about-highlight-item">
                  <div className="about-hl-icon"><FontAwesomeIcon icon={faCheck} /></div>
                  <span>Personalized financial strategies for every family</span>
                </div>
                <div className="about-highlight-item">
                  <div className="about-hl-icon"><FontAwesomeIcon icon={faCheck} /></div>
                  <span>Education-first approach to financial planning</span>
                </div>
                <div className="about-highlight-item">
                  <div className="about-hl-icon"><FontAwesomeIcon icon={faCheck} /></div>
                  <span>Lifetime partnership, not just a transaction</span>
                </div>
              </div>
              <Link to="/about" className="btn-secondary">
                Learn Our Story <FontAwesomeIcon icon={faArrowRight} />
              </Link>
            </div>
            <div className="home-about-right">
              <div className="about-cards-stack">
                <div className="about-stack-card asc-1">
                  <div className="asc-icon"><FontAwesomeIcon icon={faShieldHalved} /></div>
                  <h4>Protection First</h4>
                  <p>Every strategy starts with protecting what matters most — your family's financial wellbeing.</p>
                </div>
                <div className="about-stack-card asc-2">
                  <div className="asc-icon"><FontAwesomeIcon icon={faGraduationCap} /></div>
                  <h4>Education Driven</h4>
                  <p>We teach you to understand your finances so you can make confident decisions.</p>
                </div>
                <div className="about-stack-card asc-3">
                  <div className="asc-icon"><FontAwesomeIcon icon={faChartLine} /></div>
                  <h4>Growth Focused</h4>
                  <p>Tax-efficient strategies designed to build and preserve lasting wealth.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ====== SERVICES ====== */}
      <section className="home-services section-padding reveal">
        <div className="container">
          <div className="services-header">
            <span className="section-tag">Our Services</span>
            <h2 className="section-title">Six Pillars of Financial Protection</h2>
            <p className="section-subtitle">
              Comprehensive solutions designed to protect, grow, and secure your family's wealth at every stage of life.
            </p>
          </div>
          <div className="services-grid reveal-stagger">
            {[
              { icon: faShieldHalved, title: "Life Insurance", desc: "Term, whole, and universal life coverage tailored to protect your family when it matters most.", link: "/services/life-insurance" },
              { icon: faChartLine, title: "Retirement Planning", desc: "Strategic roadmaps for financial independence — designed around your timeline and goals.", link: "/services/retirement-planning" },
              { icon: faGraduationCap, title: "Financial Education", desc: "Our Real Secrets of Money program transforms how you think about and manage wealth.", link: "/services/financial-education" },
              { icon: faHandshake, title: "Business Protection", desc: "Key person coverage, succession planning, and group benefits for growing businesses.", link: "/services/business-protection" },
              { icon: faUsers, title: "Legacy Planning", desc: "Multi-generational wealth strategies that protect your family's future for decades.", link: "/services/legacy-planning" },
              { icon: faLightbulb, title: "Wealth Accumulation", desc: "Tax-efficient growth strategies including our exclusive Kai-Zen supplemental retirement plan.", link: "/services/wealth-accumulation" },
            ].map((s, i) => (
              <Link to={s.link} className="service-card" key={i}>
                <div className="service-card-bg"></div>
                <div className="service-card-number">0{i + 1}</div>
                <div className="service-card-icon">
                  <FontAwesomeIcon icon={s.icon} />
                </div>
                <h4>{s.title}</h4>
                <p>{s.desc}</p>
                <span className="service-card-link">
                  Learn More <FontAwesomeIcon icon={faArrowRight} />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ====== PROCESS ====== */}
      <section className="home-process section-padding reveal">
        <div className="container">
          <div className="process-header">
            <span className="section-tag">How It Works</span>
            <h2 className="section-title">Your Journey to Financial Clarity</h2>
            <p className="section-subtitle">
              Three simple steps to get started. No pressure, no obligations — just clarity and confidence.
            </p>
          </div>
          <div className="process-timeline">
            <div className="process-line"></div>
            <div className="process-step">
              <div className="process-step-marker">
                <span>01</span>
              </div>
              <div className="process-step-content">
                <h4>Discovery Call</h4>
                <p>A focused 30-minute conversation to understand your financial goals, current situation, and concerns. We listen first, always.</p>
              </div>
            </div>
            <div className="process-step">
              <div className="process-step-marker">
                <span>02</span>
              </div>
              <div className="process-step-content">
                <h4>Custom Strategy</h4>
                <p>Our team designs a personalized financial plan addressing your specific needs, timeline, risk tolerance, and family goals.</p>
              </div>
            </div>
            <div className="process-step">
              <div className="process-step-marker">
                <span>03</span>
              </div>
              <div className="process-step-content">
                <h4>Implementation & Support</h4>
                <p>We execute your plan with ongoing monitoring, regular check-ins, and proactive adjustments as your life evolves.</p>
              </div>
            </div>
          </div>
          <div className="process-cta">
            <Link to="/schedule-appointment" className="btn-primary">
              Start Your Journey <FontAwesomeIcon icon={faArrowRight} />
            </Link>
          </div>
        </div>
      </section>

      {/* ====== TESTIMONIAL ====== */}
      <section className="home-testimonial section-padding reveal">
        <div className="container">
          <div className="testimonial-wrapper">
            <div className="testimonial-quote-icon">
              <FontAwesomeIcon icon={faQuoteLeft} />
            </div>
            <blockquote className="testimonial-text">
              "Ebenezer Family Group didn't just help us find the right insurance — they 
              educated us, gave us confidence, and built a complete financial strategy 
              for our family's future. We wish we had found them sooner."
            </blockquote>
            <div className="testimonial-author">
              <div className="testimonial-stars">
                {[...Array(5)].map((_, i) => <FontAwesomeIcon key={i} icon={faStar} />)}
              </div>
              <span className="testimonial-name">— The Johnson Family, Minnesota</span>
            </div>
          </div>
        </div>
      </section>

      {/* ====== CTA ====== */}
      <section className="home-cta reveal">
        <div className="home-cta-bg">
          <div className="cta-orb cta-orb-1"></div>
          <div className="cta-orb cta-orb-2"></div>
          <div className="cta-grid-pattern"></div>
        </div>
        <div className="container home-cta-inner">
          <span className="section-tag cta-tag">Get Started Today</span>
          <h2>Ready to Build Your Family's<br/>Financial Future?</h2>
          <p>
            Schedule a free, no-obligation consultation with our team. 
            We'll listen to your goals and show you what's possible.
          </p>
          <div className="cta-buttons">
            <Link to="/schedule-appointment" className="btn-primary btn-cta">
              Book Free Consultation <FontAwesomeIcon icon={faArrowRight} />
            </Link>
            <Link to="/contact" className="btn-outline-light">
              Contact Us
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default Home;
